import * as Sentry from '@sentry/react';
import { toast } from "sonner";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  sendEmailVerification,
  EmailAuthProvider,
  reauthenticateWithCredential,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
  User as FirebaseUser
} from 'firebase/auth';
import { auth, db } from './firebase';
import { createUserProfile, getUserProfile } from './admin';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ClientEncryption } from './encryption-utils';
import { validatePassword, isCommonPassword } from './password-validation';

// Shape of Firebase/browser errors we narrow to inside catch blocks.
// Using unknown + this alias keeps us type-safe without importing FirebaseError
// everywhere (Firebase SDK can throw plain Errors for network issues too).
type AuthErrorLike = { code?: string; message?: string; name?: string; stack?: string };

export interface User {
  id: string;
  email: string;
  createdAt: string;
}

// In-memory session to store encryption key (NEVER stored in localStorage or Firestore)
interface AuthSession {
  user: User;
  encryptionKey?: CryptoKey;
}

let currentSession: AuthSession | null = null;

// Convert Firebase user to our User type
function firebaseUserToUser(firebaseUser: FirebaseUser): User {
  return {
    id: firebaseUser.uid,
    email: firebaseUser.email || '',
    createdAt: firebaseUser.metadata.creationTime || new Date().toISOString(),
  };
}

export async function signup(email: string, password: string, name?: string): Promise<User | null> {
  try {
    console.log('🔐 Starting signup process for:', email);

    // Validate password strength before creating account
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      toast.error(passwordValidation.error || 'Password does not meet security requirements');
      return null;
    }

    // Check if password is in common passwords list
    if (isCommonPassword(password)) {
      toast.error('This password is too common. Please choose a more unique password.');
      return null;
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = firebaseUserToUser(userCredential.user);

    console.log('✅ User created in Firebase Auth:', user.id);

    // Send email verification
    try {
      await sendEmailVerification(userCredential.user);
      console.log('📧 Verification email sent to:', email);
      toast.success('Account created! Please check your email to verify your account.', {
        duration: 6000,
      });
    } catch (verificationError: unknown) {
      console.error('⚠️ Failed to send verification email:', verificationError);
      toast.error('Account created but verification email failed. Please request a new one.');
    }

    // Initialize encryption for new user
    console.log('🔒 Initializing encryption...');

    // Check if Web Crypto API is available
    if (!window.crypto || !window.crypto.subtle) {
      console.error('⚠️ Web Crypto API not available. Encryption requires HTTPS or localhost.');
      toast.error('Encryption unavailable: Secure connection required');
      currentSession = { user };
    } else {
      try {
        const { key, salt } = await ClientEncryption.deriveKeyFromPassword(password);
        ClientEncryption.storeSalt(user.id, salt);

        // Store encryption key in memory session
        currentSession = { user, encryptionKey: key };
        console.log('✅ Encryption initialized successfully');
        console.log('🔑 Encryption key is now available in session');
      } catch (encryptionError: unknown) {
        const err = encryptionError as AuthErrorLike;
        console.error('⚠️ Encryption initialization failed:', encryptionError);
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
        toast.error('Warning: Encryption setup failed. Personal information will not be encrypted.');
        currentSession = { user };
      }
    }

    // Create user profile in Firestore
    try {
      console.log('📝 Creating user profile in Firestore...');
      await createUserProfile(user.id, user.email, false, name);
      console.log('✅ User profile created successfully');
    } catch (profileError: unknown) {
      console.error('❌ Failed to create user profile:', profileError);
      toast.error('Account created but profile setup failed. Please contact support.');
      // Don't return null - user is still created in Auth
    }

    Sentry.setUser({ id: user.id, email: user.email });
    return user;
  } catch (error: unknown) {
    const err = error as AuthErrorLike;
    console.error('❌ Signup error:', error);

    if (err.code === 'auth/email-already-in-use') {
      toast.error('An account with this email already exists');
    } else if (err.code === 'auth/weak-password') {
      toast.error('Password should be at least 6 characters');
    } else if (err.code === 'auth/invalid-email') {
      toast.error('Invalid email address');
    } else if (err.code === 'auth/operation-not-allowed') {
      toast.error('Email/password sign-in is not enabled. Please contact support.');
    } else if (err.code === 'auth/network-request-failed') {
      toast.error('Network error. Please check your internet connection.');
    } else {
      toast.error(`Failed to create account: ${err.message || 'Unknown error'}`);
      console.error('Full error details:', error);
    }
    return null;
  }
}

export async function login(email: string, password: string, rememberMe: boolean = true): Promise<User | null> {
  try {
    // Set persistence based on remember me checkbox
    // LOCAL: persists even after browser is closed (default)
    // SESSION: persists only for the current session (cleared when tab/window is closed)
    const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
    console.log('🔐 Setting persistence to:', rememberMe ? 'LOCAL (Remember Me)' : 'SESSION (No Remember Me)');

    try {
      await setPersistence(auth, persistence);
      console.log('✅ Persistence set successfully');
    } catch (persistenceError) {
      console.error('⚠️ Failed to set persistence:', persistenceError);
      // Continue with login even if persistence fails - Firebase will use default
    }

    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = firebaseUserToUser(userCredential.user);

    // Check if email is verified (skip in emulator mode for testing)
    const isEmulator = import.meta.env.VITE_USE_FIREBASE_EMULATOR === 'true';
    if (!userCredential.user.emailVerified && !isEmulator) {
      console.log('⚠️ Email not verified for user:', user.id);
      toast.error('Please verify your email before logging in. Check your inbox for the verification link.', {
        duration: 6000,
      });
      await signOut(auth); // Sign out unverified user
      return null;
    } else if (!userCredential.user.emailVerified && isEmulator) {
      console.log('🔧 Emulator mode: Skipping email verification for user:', user.id);
    }

    // Derive encryption key from password
    console.log('🔒 Deriving encryption key for user:', user.id);

    // Check if Web Crypto API is available
    if (!window.crypto || !window.crypto.subtle) {
      console.error('⚠️ Web Crypto API not available. Encryption requires HTTPS or localhost.');
      toast.error('Encryption unavailable: Secure connection required');
      currentSession = { user };
    } else {
      try {
        let salt = ClientEncryption.getSalt(user.id);
        console.log('🔑 Salt retrieval result:', salt ? 'Found existing salt' : 'No salt found');

        if (!salt) {
          // First login after encryption update - generate new salt
          console.log('📝 No salt found, generating new salt for existing user');
          const keyData = await ClientEncryption.deriveKeyFromPassword(password);
          salt = keyData.salt;
          ClientEncryption.storeSalt(user.id, salt);
          currentSession = { user, encryptionKey: keyData.key };
          console.log('✅ New salt generated and stored');
        } else {
          // Derive key using existing salt
          console.log('🔓 Deriving key using existing salt...');
          const keyData = await ClientEncryption.deriveKeyFromPassword(password, salt);
          currentSession = { user, encryptionKey: keyData.key };
          console.log('✅ Key derived using existing salt');
        }

        console.log('✅ Encryption key derived and session established');
        console.log('🔑 Encryption key is now available in session:', !!currentSession?.encryptionKey);
      } catch (encryptionError: unknown) {
        const err = encryptionError as AuthErrorLike;
        console.error('⚠️ Failed to derive encryption key:', encryptionError);
        console.error('Error details:', {
          name: err.name,
          message: err.message,
          stack: err.stack
        });
        toast.error('Warning: Encryption setup failed. Personal information will not be encrypted.');
        currentSession = { user };
      }
    }

    Sentry.setUser({ id: user.id, email: user.email });
    toast.success('Logged in successfully');
    return user;
  } catch (error: unknown) {
    const err = error as AuthErrorLike;
    if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
      toast.error('Invalid email or password');
    } else if (err.code === 'auth/invalid-email') {
      toast.error('Invalid email address');
    } else if (err.code === 'auth/too-many-requests') {
      toast.error('Too many failed attempts. Please try again later');
    } else {
      toast.error('Login failed');
      console.error('Login error:', error);
    }
    return null;
  }
}

export async function signInWithGoogle(): Promise<User | null> {
  try {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    const fbUser = userCredential.user;
    const user = firebaseUserToUser(fbUser);

    // Google users are always email-verified — no check needed

    // Set session without encryption key (no password to derive from)
    currentSession = { user };

    // Create Firestore profile if this is first sign-in
    const existingProfile = await getUserProfile(user.id);
    if (!existingProfile) {
      await createUserProfile(user.id, user.email, false, fbUser.displayName || undefined);
    }

    Sentry.setUser({ id: user.id, email: user.email });
    toast.success('Logged in successfully');
    return user;
  } catch (error: unknown) {
    const err = error as AuthErrorLike;
    if (
      err.code === 'auth/popup-closed-by-user' ||
      err.code === 'auth/cancelled-popup-request'
    ) {
      // User dismissed — no toast
    } else if (err.code === 'auth/popup-blocked') {
      toast.error('Popup blocked. Please allow popups for this site.');
    } else if (err.code === 'auth/account-exists-with-different-credential') {
      toast.error('An account already exists with this email. Please log in with your password.');
    } else {
      toast.error('Google sign-in failed. Please try again.');
      console.error('Google sign-in error:', error);
    }
    return null;
  }
}

export async function signInWithMicrosoft(): Promise<User | null> {
  try {
    const provider = new OAuthProvider('microsoft.com');
    const userCredential = await signInWithPopup(auth, provider);
    const fbUser = userCredential.user;
    const user = firebaseUserToUser(fbUser);

    // Microsoft users are considered email-verified by the provider — no check needed

    // Set session without encryption key (no password to derive from)
    currentSession = { user };

    // Create Firestore profile if this is first sign-in
    const existingProfile = await getUserProfile(user.id);
    if (!existingProfile) {
      await createUserProfile(user.id, user.email, false, fbUser.displayName || undefined);
    }

    Sentry.setUser({ id: user.id, email: user.email });
    toast.success('Logged in successfully');
    return user;
  } catch (error: unknown) {
    const err = error as AuthErrorLike;
    if (
      err.code === 'auth/popup-closed-by-user' ||
      err.code === 'auth/cancelled-popup-request'
    ) {
      // User dismissed — no toast
    } else if (err.code === 'auth/popup-blocked') {
      toast.error('Popup blocked. Please allow popups for this site.');
    } else if (err.code === 'auth/account-exists-with-different-credential') {
      toast.error('An account already exists with this email. Please log in with your password.');
    } else if (err.code === 'auth/operation-not-allowed') {
      toast.error('Microsoft sign-in is not enabled. Please contact support.');
      console.error('Microsoft sign-in error:', error);
    } else {
      toast.error('Microsoft sign-in failed. Please try again.');
      console.error('Microsoft sign-in error:', error);
    }
    return null;
  }
}

// ─── Email-link (passwordless / magic link) sign-in ─────────────────────

/**
 * The bootstrap admin email. The first user to sign in with this email via a
 * magic link is granted admin rights. Overridable via VITE_BOOTSTRAP_ADMIN_EMAIL.
 */
const BOOTSTRAP_ADMIN_EMAIL = (
  import.meta.env.VITE_BOOTSTRAP_ADMIN_EMAIL || 'lpcbandeira@gmail.com'
).toLowerCase();

/** True if the current URL is a Firebase email sign-in link. */
export function isEmailLinkSignIn(): boolean {
  return isSignInWithEmailLink(auth, window.location.href);
}

/**
 * Send a passwordless "magic link" to the given email.
 * Stores the email in localStorage so it can be recovered when the link is opened.
 */
export async function sendMagicLink(email: string): Promise<void> {
  const actionCodeSettings = {
    url: (import.meta.env.VITE_APP_URL || window.location.origin) + '/',
    handleCodeInApp: true,
  };
  await sendSignInLinkToEmail(auth, email, actionCodeSettings);
  window.localStorage.setItem('emailForSignIn', email);
  toast.success('Magic link sent! Check your inbox.');
}

/**
 * Complete a passwordless email-link sign-in. Call once on app load when
 * isEmailLinkSignIn() returns true. Grants admin rights when the email matches
 * the bootstrap admin email.
 */
export async function completeEmailLinkSignIn(): Promise<User | null> {
  try {
    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return null;
    }

    let email = window.localStorage.getItem('emailForSignIn');
    if (!email) {
      email = window.prompt('Please enter your email to confirm sign-in') || '';
    }
    if (!email) {
      toast.error('No email found for this sign-in link.');
      return null;
    }

    const result = await signInWithEmailLink(auth, email, window.location.href);
    window.localStorage.removeItem('emailForSignIn');

    const user = firebaseUserToUser(result.user);
    currentSession = { user }; // passwordless → no encryption key

    // Grant admin rights to the bootstrap email on first sign-in.
    const isAdmin = email.toLowerCase() === BOOTSTRAP_ADMIN_EMAIL;
    const existingProfile = await getUserProfile(user.id);
    if (!existingProfile) {
      await createUserProfile(user.id, user.email, isAdmin, undefined);
    } else if (isAdmin) {
      // Promote an existing bootstrap account without wiping its other fields.
      await setDoc(doc(db, 'userProfiles', user.id), { isAdmin: true }, { merge: true });
    }

    Sentry.setUser({ id: user.id, email: user.email });
    toast.success(isAdmin ? 'Signed in as admin' : 'Signed in successfully');
    return user;
  } catch (error) {
    const err = error as AuthErrorLike;
    console.error('Email link sign-in failed:', error);
    if (err.code === 'auth/invalid-action-code') {
      toast.error('This sign-in link is invalid or has expired.');
    } else if (err.code === 'auth/operation-not-allowed') {
      toast.error('Email link sign-in is not enabled for this project.');
    } else {
      toast.error('Sign-in failed. Please try again.');
    }
    return null;
  }
}

export async function logout(): Promise<void> {
  try {
    await signOut(auth);
    // Clear encryption session (keys exist only in memory)
    currentSession = null;
    Sentry.setUser(null);
    console.log('🔒 Encryption session cleared');
    toast.success('Logged out successfully');
  } catch (error) {
    toast.error('Logout failed');
    console.error('Logout error:', error);
  }
}

export function getCurrentUser(): User | null {
  // Guard against `auth` being undefined — e.g., when Firebase init failed at
  // module load (missing env vars, chunk-load race) and firebase.ts swallowed
  // the error. Without this guard the call throws `Cannot read properties of
  // undefined (reading 'currentUser')` during component render.
  const firebaseUser = auth?.currentUser;
  if (!firebaseUser) return null;

  return firebaseUserToUser(firebaseUser);
}

/**
 * Get the encryption key from the current session
 * Returns undefined if no session or no encryption key available
 *
 * @returns CryptoKey for encryption/decryption or undefined
 */
export function getEncryptionKey(): CryptoKey | undefined {
  return currentSession?.encryptionKey;
}

/**
 * Set the encryption key for the current session
 * Used to re-activate encryption after entering password
 *
 * @param key The encryption key to set
 */
export function setEncryptionKey(key: CryptoKey): void {
  if (currentSession) {
    currentSession = { ...currentSession, encryptionKey: key };
    console.log('✅ [Auth] Encryption key set successfully');
  } else {
    console.error('❌ [Auth] Cannot set encryption key: No active session');
  }
}

/**
 * Debug function to check current session status
 * Can be called from browser console: window.debugAuthSession()
 */
export function debugAuthSession() {
  console.log('🔍 Debug: Current Auth Session Status');
  console.log('=====================================');
  console.log('Has session:', !!currentSession);
  console.log('User ID:', currentSession?.user?.id || 'N/A');
  console.log('User email:', currentSession?.user?.email || 'N/A');
  console.log('Has encryption key:', !!currentSession?.encryptionKey);
  console.log('Firebase user:', auth?.currentUser?.uid || 'Not logged in');
  console.log('Web Crypto available:', !!(window.crypto && window.crypto.subtle));

  if (currentSession?.user?.id) {
    const salt = ClientEncryption.getSalt(currentSession.user.id);
    console.log('Salt stored:', !!salt);
    console.log('LocalStorage keys:', Object.keys(localStorage).filter(k => k.startsWith('rc_salt_')));
  }
  console.log('=====================================');

  return {
    hasSession: !!currentSession,
    userId: currentSession?.user?.id,
    hasEncryptionKey: !!currentSession?.encryptionKey,
    firebaseUser: auth?.currentUser?.uid,
    webCryptoAvailable: !!(window.crypto && window.crypto.subtle)
  };
}

// Make debug function available globally in development
if (typeof window !== 'undefined') {
  (window as Window & { debugAuthSession?: typeof debugAuthSession }).debugAuthSession = debugAuthSession;
}

export function isAuthenticated(): boolean {
  return auth?.currentUser != null;
}

// Subscribe to auth state changes
export function onAuthStateChange(callback: (user: User | null) => void): () => void {
  console.log('🔄 Setting up auth state listener');
  if (!auth) {
    console.error('[auth] onAuthStateChange called but Firebase auth is not initialized');
    // Fire callback once with null so pages fall through to the anonymous path
    // instead of crashing. Return a no-op unsubscribe.
    queueMicrotask(() => callback(null));
    return () => {};
  }
  return onAuthStateChanged(auth, (firebaseUser) => {
    console.log('🔔 Auth state changed event received:', firebaseUser ? `User: ${firebaseUser.uid}` : 'No user');
    if (firebaseUser) {
      const user = firebaseUserToUser(firebaseUser);
      console.log('🔄 Auth state changed, user:', user.id);
      console.log('📊 Current session status:', {
        hasSession: !!currentSession,
        sessionUserId: currentSession?.user?.id,
        hasEncryptionKey: !!currentSession?.encryptionKey,
        matchingUser: currentSession?.user?.id === user.id
      });

      // Restore session but WITHOUT encryption key
      // Encryption key can only be derived from password (during login/signup)
      if (!currentSession || currentSession.user.id !== user.id) {
        console.log('🔒 Session restored without encryption key (will be available after next login)');
        currentSession = { user };
      } else {
        console.log('✅ Session already exists, preserving encryption key');
        // Update user data but preserve encryption key
        currentSession = { user, encryptionKey: currentSession.encryptionKey };
      }

      // Use setTimeout to ensure callback runs after encryption key is set up
      // This gives the login/signup function time to complete
      setTimeout(() => callback(user), 0);
    } else {
      console.log('🚪 User logged out, clearing session');
      currentSession = null;
      callback(null);
    }
  });
}

// Send password reset email
export async function resetPassword(email: string): Promise<boolean> {
  try {
    await sendPasswordResetEmail(auth, email);
    toast.success('Password reset email sent. Please check your inbox.');
    return true;
  } catch (error: unknown) {
    const err = error as AuthErrorLike;
    console.error('Password reset error:', error);

    if (err.code === 'auth/user-not-found') {
      // For security reasons, don't reveal if email exists
      // Still show success to prevent email enumeration
      toast.success('If an account exists with this email, a password reset link has been sent.');
      return true;
    } else if (err.code === 'auth/invalid-email') {
      toast.error('Invalid email address');
    } else if (err.code === 'auth/too-many-requests') {
      toast.error('Too many requests. Please try again later');
    } else if (err.code === 'auth/network-request-failed') {
      toast.error('Network error. Please check your internet connection.');
    } else {
      toast.error('Failed to send reset email. Please try again.');
    }
    return false;
  }
}

// Resend email verification
export async function resendVerificationEmail(): Promise<boolean> {
  try {
    const user = auth?.currentUser;
    if (!user) {
      toast.error('No user logged in');
      return false;
    }

    if (user.emailVerified) {
      toast.info('Your email is already verified');
      return true;
    }

    await sendEmailVerification(user);
    toast.success('Verification email sent. Please check your inbox.');
    return true;
  } catch (error: unknown) {
    const err = error as AuthErrorLike;
    console.error('Resend verification error:', error);

    if (err.code === 'auth/too-many-requests') {
      toast.error('Too many requests. Please wait a few minutes before trying again.');
    } else if (err.code === 'auth/network-request-failed') {
      toast.error('Network error. Please check your internet connection.');
    } else {
      toast.error('Failed to send verification email. Please try again.');
    }
    return false;
  }
}

// Add user to waitlist (no authentication, just Firestore entry)
export async function addToWaitlist(
  email: string,
  name: string,
  ageConfirmed: boolean,
  isPublic: boolean = false,
  partnerEmail?: string
): Promise<boolean> {
  try {
    console.log('📝 Adding to waitlist:', email, 'isPublic:', isPublic);

    // Create a unique ID for the waitlist entry (using email as key to prevent duplicates)
    const waitlistId = email.replace(/[^a-zA-Z0-9]/g, '_'); // Sanitize email for Firestore doc ID
    const waitlistRef = doc(db, 'waitlist', waitlistId);

    await setDoc(waitlistRef, {
      email,
      name,
      ageConfirmed, // User confirmed they are 18+
      nameEncrypted: false,
      isPublic, // true = joined via /signup (waiting-public), false = joined via /waiting (invited)
      source: 'public', // 'public' here; 'direct_invite' is set by adminDirectInvite Cloud Function
      emailTone: 'friendly', // default tone — admin can change before sending invite
      ...(partnerEmail && { partnerEmail }), // Only include if provided
      createdAt: new Date().toISOString(),
      createdAtTimestamp: serverTimestamp(),
      status: 'pending'
    });

    console.log('✅ Added to waitlist successfully');
    toast.success('Welcome to the waitlist! We\'ll be in touch soon.');
    return true;
  } catch (error: unknown) {
    console.error('❌ Waitlist error:', error);
    toast.error('Failed to join waitlist. Please try again.');
    return false;
  }
}

/**
 * Re-authenticate user with their password (required for sensitive operations like account deletion)
 */
export async function reauthenticateUser(password: string): Promise<boolean> {
  try {
    const user = auth?.currentUser;
    if (!user || !user.email) {
      console.error('❌ No current user or email');
      return false;
    }

    console.log('🔐 Re-authenticating user:', user.email);

    const credential = EmailAuthProvider.credential(user.email, password);
    await reauthenticateWithCredential(user, credential);

    console.log('✅ Re-authentication successful');
    return true;
  } catch (error: unknown) {
    const err = error as AuthErrorLike;
    console.error('❌ Re-authentication failed:', error);
    if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
      toast.error('Incorrect password. Please try again.');
    } else {
      toast.error('Re-authentication failed. Please try again.');
    }
    return false;
  }
}
